'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { History, Loader2, Ticket } from 'lucide-react';
import api from '@/app/lib/axios';
import MatrixGrid from '@/app/components/MatrixGrid';
import Pagination from '@/app/store/components/Pagination';
import RaffleRow from '@/app/raffles/components/RaffleRow';
import { useAuth } from '@/app/hooks/context/AuthContext';
import { CommunityRaffle, RafflesResponse } from '@/types/raffle';

type Tab = 'current' | 'past';

// Refresh cadence for counts/status — the draw itself runs server-side every minute.
const POLL_MS = 30_000;

const STEPS = [
    { step: '1', title: 'Earn Points', desc: 'Watch and chat on Kick to collect Kick Points' },
    { step: '2', title: 'Enter Tickets', desc: '1 ticket = 1 Kick Point, within the raffle’s min – max' },
    { step: '3', title: 'Wait for Draw', desc: 'The winner is drawn automatically at draw time' },
    { step: '4', title: 'Get the Prize', desc: 'Kick Points land instantly, Stake tips are sent by the team' },
];

export default function RafflesPage() {
    const { user } = useAuth();
    const [tab, setTab] = useState<Tab>('current');
    const [current, setCurrent] = useState<CommunityRaffle[]>([]);
    const [past, setPast] = useState<CommunityRaffle[]>([]);
    const [pastPage, setPastPage] = useState(1);
    const [pastLastPage, setPastLastPage] = useState(1);
    const [pastTotal, setPastTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    // undefined = the user hasn't touched anything yet → auto-open a lone raffle.
    const [openId, setOpenId] = useState<number | null | undefined>(undefined);
    const [now, setNow] = useState(() => Date.now());
    const pastPageRef = useRef(pastPage);
    pastPageRef.current = pastPage;

    const fetchRaffles = useCallback(async (page: number) => {
        try {
            const { data } = await api.get<RafflesResponse>('raffles', { params: { page } });
            setCurrent(data.current);
            setPast(data.past.data);
            setPastLastPage(data.past.last_page);
            setPastTotal(data.past.total);
        } catch (error) {
            console.error('Error fetching raffles:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Refetch on page change and on login/logout (my_tickets is per viewer).
    useEffect(() => {
        fetchRaffles(pastPage);
    }, [fetchRaffles, pastPage, user?.id]);

    useEffect(() => {
        const poll = setInterval(() => fetchRaffles(pastPageRef.current), POLL_MS);
        const tick = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            clearInterval(poll);
            clearInterval(tick);
        };
    }, [fetchRaffles]);

    // A countdown hitting zero means the phase changed — pull the new state.
    const nextBoundary = current
        .map(r => new Date(r.status === 'upcoming' ? r.starts_at : r.ends_at).getTime())
        .filter(t => t > now)
        .sort((a, b) => a - b)[0];
    useEffect(() => {
        if (!nextBoundary) return;
        const t = setTimeout(() => fetchRaffles(pastPageRef.current), nextBoundary - Date.now() + 1500);
        return () => clearTimeout(t);
    }, [nextBoundary, fetchRaffles]);

    const handleUpdated = (raffle: CommunityRaffle) => {
        setCurrent(list => list.map(r => (r.id === raffle.id ? raffle : r)));
    };

    const list = tab === 'current' ? current : past;
    const expandedId = openId === undefined ? (list.length === 1 ? list[0].id : null) : openId;
    const toggle = (id: number) => setOpenId(expandedId === id ? null : id);
    const switchTab = (next: Tab) => {
        setTab(next);
        setOpenId(undefined);
    };

    return (
        <div className="mt-15 text-gray-200 min-h-screen overflow-hidden relative">
            <MatrixGrid />
            <div className="relative z-10 max-w-7xl mx-auto px-4 py-16">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-6xl font-mono font-bold tracking-widest mb-4">
                        <span className="bg-gradient-to-r from-green-400 via-emerald-300 to-green-500 bg-clip-text text-transparent">
                            COMMUNITY RAFFLES
                        </span>
                    </h1>
                    <p className="uppercase text-green-300 font-mono text-sm md:text-base">
                        <span className="inline-block animate-pulse">{'['}</span> Turn your Kick Points into tickets <span className="inline-block animate-pulse">{']'}</span>
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex justify-center gap-2 mb-8">
                    {([
                        { id: 'current', label: 'Active', icon: Ticket, count: current.length },
                        { id: 'past', label: 'Past', icon: History, count: pastTotal },
                    ] as const).map(({ id, label, icon: Icon, count }) => (
                        <button
                            key={id}
                            onClick={() => switchTab(id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono text-sm font-bold transition-all duration-200 ${
                                tab === id
                                    ? 'bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-white'
                                    : 'backdrop-blur text-gray-400 hover:text-white'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                            <span className="rounded-md bg-black/20 px-1.5 text-xs tabular-nums">{count}</span>
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex items-center justify-center gap-2 font-mono text-gray-400 py-24">
                        <Loader2 className="w-5 h-5 animate-spin" /> Loading raffles…
                    </div>
                ) : list.length === 0 ? (
                    tab === 'current' ? (
                        <EmptyState
                            title="No raffles running right now"
                            text="New raffles drop regularly — keep stacking Kick Points so you're ready."
                        />
                    ) : (
                        <EmptyState title="No past raffles yet" text="Finished raffles and their winners will show up here." />
                    )
                ) : (
                    <>
                        <div className="space-y-3">
                            {list.map(raffle => (
                                <RaffleRow
                                    key={raffle.id}
                                    raffle={raffle}
                                    now={now}
                                    open={expandedId === raffle.id}
                                    onToggle={() => toggle(raffle.id)}
                                    onUpdated={handleUpdated}
                                />
                            ))}
                        </div>
                        {tab === 'past' && pastLastPage > 1 && (
                            <div className="mt-8">
                                <Pagination currentPage={pastPage} lastPage={pastLastPage} onPageChange={setPastPage} />
                            </div>
                        )}
                    </>
                )}

                {/* How it works */}
                <div className="w-full mt-12 backdrop-blur rounded-2xl overflow-hidden border-b border-green-400/30 p-8">
                    <h2 className="text-2xl md:text-4xl font-mono font-bold text-center mb-8 text-green-400">
                        <span className="inline-block animate-pulse">{'['}</span> HOW IT WORKS <span className="inline-block animate-pulse">{']'}</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        {STEPS.map(item => (
                            <div key={item.step} className="text-center group/step">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-500 rounded-full text-black text-xl font-bold mb-3 transition-transform duration-300 group-hover/step:scale-110 group-hover/step:rotate-12">
                                    {item.step}
                                </div>
                                <h4 className="text-lg font-mono font-bold text-green-400 mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-300 font-mono">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function EmptyState({ title, text }: { title: string; text: string }) {
    return (
        <div className="flex flex-col items-center rounded-2xl backdrop-blur py-20 px-6 text-center">
            <div className="rounded-2xl bg-green-500/10 p-4 text-green-400">
                <Ticket className="w-8 h-8" />
            </div>
            <h2 className="mt-4 text-lg font-mono font-bold text-white">{title}</h2>
            <p className="mt-1 text-sm font-mono text-gray-400 max-w-sm">{text}</p>
        </div>
    );
}
